import React, { useEffect, useState, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import { fetchClinicById, fetchClinics } from "../../store/thunks/clinicThunk";
import { fetchPayrollCohorts, createPayrollCohort, updatePayrollCohort, deletePayrollCohort } from "../../store/thunks/payrollCohortThunk";
import Table from "../../applicationUi/components/Table";
import { logError, logInfo } from "../../utils/logger";
import ClinicDetails from "./ClinicDetails";
import ClinicOverview from "./ClinicOverview";
import AddClinicModal from "./AddClinicModal";
import { toast } from 'react-toastify';
import { Listbox, Transition, Dialog } from "@headlessui/react";
import {
  CheckIcon,
  ChevronUpDownIcon,
  XMarkIcon,
  PlusIcon,
  PencilIcon,
  TrashIcon,
  InformationCircleIcon,
} from "@heroicons/react/20/solid";

export default function ClinicPage({ clickedItem }) {
  const dispatch = useDispatch();
  const {
    clinics = [],
    pagination = { currentPage: 1, pageSize: 10 },
    status: clinicStatus,
    error: clinicError,
  } = useSelector((state) => state.clinics);
  const payrollCohorts = useSelector((state) => state.payrollCohorts?.payrollCohorts || []);

  const [selectedClinic, setSelectedClinic] = useState(null);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  
  // Cohort filtering state
  const [selectedPayrollCohort, setSelectedPayrollCohort] = useState(null);
  
  // Cohort management modal states
  const [showCohortModal, setShowCohortModal] = useState(false);
  const [editingCohort, setEditingCohort] = useState(null);
  const [cohortFormData, setCohortFormData] = useState({ name: '', description: '' });
  const [cohortModalLoading, setCohortModalLoading] = useState(false);

  useEffect(() => {
    if (clickedItem && clickedItem._id) {
      logInfo("Fetching specific clinic", { clinicId: clickedItem._id });
      dispatch(fetchClinicById(clickedItem._id))
        .unwrap()
        .then((clinic) => {
          logInfo("Specific clinic fetched successfully", {
            clinicId: clinic._id,
          });
          setSelectedClinic(clinic);
        })
        .catch((error) => {
          logError("Failed to fetch specific clinic", {
            error,
            clinicId: clickedItem._id,
          });
          console.error("Failed to fetch specific clinic:", error);
        });
    } else {
      logInfo("Fetching clinics and payroll cohorts", {
        page: pagination.currentPage,
        limit: pagination.pageSize,
      });
      Promise.all([
        dispatch(
          fetchClinics({
            page: pagination.currentPage,
            limit: pagination.pageSize,
          })
        ).unwrap(),
        dispatch(fetchPayrollCohorts()).unwrap()
      ])
        .then(() => {
          logInfo("Clinics and payroll cohorts fetched successfully", {
            clinicCount: clinics.length,
          });
        })
        .catch((error) => {
          logError("Failed to fetch data", { error });
          console.error("Failed to fetch data:", error);
        });
    }
  }, [
    dispatch,
    pagination.currentPage,
    pagination.pageSize,
    clickedItem?._id,
  ]);

  const handleClinicClick = (clinicId) => {
    console.log('[Clinic Page] handleClinicClick called with ID:', clinicId);
    console.log('[Clinic Page] Clinic status:', clinicStatus);
    console.log('[Clinic Page] Available clinics:', clinics.map(c => ({ id: c._id, name: c.name })));
    
    // Ensure clinics are loaded before proceeding
    if (clinicStatus === 'loading' || !clinics.length) {
      console.warn('[Clinic Page] Clinics not loaded yet, deferring selection');
      return;
    }
    
    const clinic = clinics.find((clinic) => clinic._id === clinicId);
    console.log('[Clinic Page] Found clinic:', clinic ? { id: clinic._id, name: clinic.name } : null);
    
    if (!clinic) {
      console.error('[Clinic Page] No clinic found with ID:', clinicId, 'in clinics:', clinics.map(c => c._id));
      // Try to fetch the specific clinic if it's not in the list
      console.log('[Clinic Page] Attempting to fetch clinic by ID:', clinicId);
      dispatch(fetchClinicById(clinicId))
        .unwrap()
        .then((fetchedClinic) => {
          console.log('[Clinic Page] Successfully fetched clinic:', fetchedClinic.name);
          setSelectedClinic(fetchedClinic);
        })
        .catch((error) => {
          console.error('[Clinic Page] Failed to fetch clinic:', error);
        });
      return;
    }
    
    setSelectedClinic(clinic);
  };

  const handleUpdateClinic = (updatedClinic) => {
    logInfo("Clinic updated", {
      componentName: "ClinicPage",
      action: "updateClinic",
      clinicId: updatedClinic?._id,
    });
    
    console.log('[Clinic Page] Clinic update received:', {
      updatedClinicId: updatedClinic?._id,
      updatedClinicName: updatedClinic?.name,
      currentSelectedId: selectedClinic?._id
    });
    
    // Update the selected clinic with the new data and refresh the clinics list
    if (updatedClinic) {
      setSelectedClinic(updatedClinic);
      // Refresh clinics list to ensure consistency
      handleRefresh();
    }
  };

  const handleAddClinic = () => {
    logInfo("Add Clinic action initiated", {
      componentName: "ClinicPage",
      action: "addClinic",
    });
    setIsAddModalOpen(true);
  };

  const handleRefresh = () => {
    Promise.all([
      dispatch(fetchClinics({
        page: pagination.currentPage,
        limit: pagination.pageSize,
      })).unwrap(),
      dispatch(fetchPayrollCohorts()).unwrap()
    ])
      .then(() => {
        logInfo("Data refreshed successfully", {
          componentName: "ClinicPage",
          action: "refreshData",
        });
      })
      .catch((error) => {
        logError("Failed to refresh data", {
          componentName: "ClinicPage",
          action: "refreshData",
          error: error.message,
        });
      });
  };

  const handleClinicCreated = (newClinic) => {
    logInfo("Clinic created successfully", {
      componentName: "ClinicPage",
      clinicId: newClinic._id,
      clinicName: newClinic.name,
    });
    
    // Refresh the clinics list to include the new clinic
    handleRefresh();
  };

  // Cohort management functions
  const handleCreateCohort = () => {
    setEditingCohort(null);
    setCohortFormData({ name: '', description: '' });
    setShowCohortModal(true);
  };
  
  const handleEditCohort = (cohort) => {
    setEditingCohort(cohort);
    setCohortFormData({ name: cohort.name, description: cohort.description || '' });
    setShowCohortModal(true);
  };
  
  const handleDeleteCohort = async (cohort) => {
    // TODO: Replace with proper confirmation dialog component from @src/dialogs/
    const confirmed = window.confirm(`Are you sure you want to delete the "${cohort.name}" cohort? This action cannot be undone.`);
    if (!confirmed) {
      return;
    }
    
    try {
      await dispatch(deletePayrollCohort(cohort._id)).unwrap();
      // If the deleted cohort was selected, reset to "All"
      if (selectedPayrollCohort?._id === cohort._id) {
        setSelectedPayrollCohort(null);
      }
      handleRefresh(); // Refresh clinic data
      toast.success('Cohort deleted successfully');
    } catch (error) {
      toast.error(`Failed to delete cohort: ${error}`);
    }
  };
  
  const handleCohortSubmit = async (e) => {
    e.preventDefault();
    if (!cohortFormData.name.trim()) {
      toast.warning('Cohort name is required');
      return;
    }
    
    setCohortModalLoading(true);
    try {
      if (editingCohort && editingCohort._id) {
        // Update existing cohort
        await dispatch(updatePayrollCohort({
          id: editingCohort._id,
          cohortData: cohortFormData
        })).unwrap();
      } else {
        // Create new cohort
        await dispatch(createPayrollCohort(cohortFormData)).unwrap();
      }
      setShowCohortModal(false);
      setEditingCohort(null);
      handleRefresh(); // Refresh data
    } catch (error) {
      toast.error(`Failed to ${editingCohort && editingCohort._id ? 'update' : 'create'} cohort: ${error}`);
    } finally {
      setCohortModalLoading(false);
    }
  };

  const handleCloseClinicDetails = () => {
    logInfo("Edit clinic closed", {
      componentName: "ClinicPage",
      action: "closeEditClinic",
    });
    setSelectedClinic(null);
  };

  // Filter clinics by selected cohort
  const filteredClinics = useMemo(() => {
    if (!selectedPayrollCohort) {
      return clinics;
    }
    return clinics.filter(clinic => 
      clinic.payrollCohort?._id === selectedPayrollCohort._id
    );
  }, [clinics, selectedPayrollCohort]);

  if (clinicStatus === "loading") {
    return <div>Loading...</div>;
  }

  if (clinicStatus === "failed") {
    return <div>Error: {clinicError}</div>;
  }

  return (
    <>
      {selectedClinic ? (
        <ClinicOverview
          selectedClinic={selectedClinic}
          onClose={handleCloseClinicDetails}
          onUpdateClinic={handleUpdateClinic}
        />
      ) : (
        <div className="pt-4">
          {/* Cohort Filter Section */}
          <div className="mb-6 bg-white p-4 rounded-sm shadow-sm ring-1 ring-gray-900/5">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
              <div className="flex items-end gap-4">
                {/* Payroll Cohort selector */}
                <div className="flex items-end gap-2">
                  <Listbox value={selectedPayrollCohort} onChange={setSelectedPayrollCohort}>
                    <div className="relative w-full min-w-[200px] max-w-[250px]">
                      <Listbox.Label className="block text-sm font-medium text-gray-700 flex items-center gap-1">
                        Filter by Payroll Cohort
                        <button
                          type="button"
                          onClick={handleCreateCohort}
                          className="inline-flex items-center text-gray-400 hover:text-gray-600 transition-colors"
                          title="Manage Payroll Cohorts">
                          <InformationCircleIcon className="h-4 w-4" />
                        </button>
                      </Listbox.Label>
                      <Listbox.Button className="relative mt-1 w-full cursor-pointer rounded-sm border border-gray-200 bg-white py-2 pl-3 pr-10 text-left text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500">
                        <span className="block truncate">
                          {selectedPayrollCohort ? selectedPayrollCohort.name : "All Cohorts"}
                        </span>
                        <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                          <ChevronUpDownIcon className="h-4 w-4 text-gray-400" />
                        </span>
                      </Listbox.Button>
                      <Transition
                        as={React.Fragment}
                        leave="transition ease-in duration-100"
                        leaveFrom="opacity-100"
                        leaveTo="opacity-0"
                      >
                        <Listbox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-sm border border-gray-200 bg-white py-1 text-sm shadow-lg focus:outline-none">
                          <Listbox.Option
                            value={null}
                            className={({ active }) =>
                              `relative cursor-pointer select-none py-2 pl-10 pr-4 ${
                                active ? "bg-gray-50 text-gray-900" : "text-gray-700"
                              }`
                            }
                          >
                            {({ selected }) => (
                              <>
                                <span className={`block truncate ${selected ? "font-medium" : "font-normal"}`}>
                                  All Cohorts
                                </span>
                                {selected && (
                                  <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-indigo-600">
                                    <CheckIcon className="h-4 w-4" />
                                  </span>
                                )}
                              </>
                            )}
                          </Listbox.Option>
                          {payrollCohorts.map((cohort) => {
                            const clinicsCount = clinics.filter(c => c.payrollCohort?._id === cohort._id).length;
                            return (
                              <Listbox.Option
                                key={cohort._id}
                                value={cohort}
                                className={({ active }) =>
                                  `relative cursor-pointer select-none py-2 pl-10 pr-4 ${
                                    active ? "bg-indigo-50 text-indigo-900" : "text-gray-700"
                                  }`
                                }
                              >
                                {({ selected }) => (
                                  <>
                                    <span className={`block truncate ${selected ? "font-medium" : "font-normal"}`}>
                                      {cohort.name} ({clinicsCount})
                                    </span>
                                    {selected && (
                                      <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-indigo-600">
                                        <CheckIcon className="h-4 w-4" />
                                      </span>
                                    )}
                                  </>
                                )}
                              </Listbox.Option>
                            );
                          })}
                        </Listbox.Options>
                      </Transition>
                    </div>
                  </Listbox>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-2">
                {selectedPayrollCohort && (
                  <button
                    onClick={() => setSelectedPayrollCohort(null)}
                    className="inline-flex items-center px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                  >
                    Clear Filter
                  </button>
                )}
                <button
                  onClick={handleRefresh}
                  className="inline-flex items-center px-3 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                >
                  Refresh
                </button>
              </div>
            </div>

            {/* Filter Status */}
            {selectedPayrollCohort && (
              <div className="mt-3 flex items-center gap-2 text-sm text-gray-600">
                <span>Active Filter:</span>
                <span className="inline-flex items-center px-2 py-1 rounded-sm text-xs font-medium bg-blue-100 text-blue-800">
                  Cohort: {selectedPayrollCohort.name}
                  <button
                    onClick={() => setSelectedPayrollCohort(null)}
                    className="ml-1 hover:text-blue-600"
                  >
                    <XMarkIcon className="h-3 w-3" />
                  </button>
                </span>
              </div>
            )}
          </div>

          <Table
            columns={[
              { header: "Name", accessor: "name" },
              {
                header: "Address",
                accessor: (row) => row.address?.street || "N/A",
              },
              { header: "Email", accessor: "email" },
              { header: "Phone", accessor: "phoneNumber" },
              {
                header: "Payroll Cohort",
                accessor: (row) => row.payrollCohort?.name || "Unassigned",
              },
            ]}
            data={filteredClinics || []}
            onRowClick={handleClinicClick}
            actions={[{ label: "Add Clinic", onClick: handleAddClinic }]}
            title={`Clinics ${selectedPayrollCohort ? `(${selectedPayrollCohort.name})` : ''}`}
            description={`A list of ${selectedPayrollCohort ? `clinics in the ${selectedPayrollCohort.name} cohort` : 'all clinics connected to your account'}.`}
          />
        </div>
      )}

      <AddClinicModal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        onClinicCreated={handleClinicCreated}
      />
      
      {/* Cohort Management Modal */}
      <Transition appear show={showCohortModal} as={React.Fragment}>
        <Dialog as="div" className="relative z-50" onClose={() => setShowCohortModal(false)}>
          <Transition.Child
            as={React.Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0">
            <div className="fixed inset-0 bg-black bg-opacity-25" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4">
              <Transition.Child
                as={React.Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95">
                <Dialog.Panel className="w-full max-w-2xl transform overflow-hidden bg-white p-6 align-middle shadow-xl transition-all rounded-sm">
                  <div className="flex items-center justify-between border-b border-gray-100 pb-3">
                    <Dialog.Title as="h3" className="text-lg font-medium leading-6 text-gray-900">
                      {editingCohort ? 'Edit Payroll Cohort' : 'Manage Payroll Cohorts'}
                    </Dialog.Title>
                    <button
                      type="button"
                      className="text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-gray-500/20"
                      onClick={() => setShowCohortModal(false)}>
                      <XMarkIcon className="h-5 w-5" aria-hidden="true" />
                    </button>
                  </div>

                  <div className="mt-4">
                    {!editingCohort ? (
                      /* Cohort List View */
                      <div className="space-y-4">
                        <div className="flex items-center justify-between">
                          <p className="text-sm text-gray-600">
                            Manage payroll cohorts for organizing clinics into processing groups.
                          </p>
                          <button
                            type="button"
                            onClick={() => {
                              setCohortFormData({ name: '', description: '' });
                              setEditingCohort({ _id: null }); // Set as new cohort object
                            }}
                            className="inline-flex items-center gap-1.5 rounded border border-indigo-600 bg-indigo-600 px-3 py-1.5 text-sm font-medium text-white shadow-sm transition-colors hover:bg-indigo-500">
                            <PlusIcon className="h-4 w-4" />
                            Add Cohort
                          </button>
                        </div>
                        
                        <div className="divide-y divide-gray-200">
                          {payrollCohorts.map((cohort) => (
                            <div key={cohort._id} className="flex items-center justify-between py-3">
                              <div>
                                <h4 className="text-sm font-medium text-gray-900">{cohort.name}</h4>
                                {cohort.description && (
                                  <p className="text-sm text-gray-500">{cohort.description}</p>
                                )}
                                <p className="text-xs text-gray-400">
                                  {clinics.filter(c => c.payrollCohort?._id === cohort._id).length} clinics assigned
                                </p>
                              </div>
                              <div className="flex gap-2">
                                <button
                                  type="button"
                                  onClick={() => handleEditCohort(cohort)}
                                  className="inline-flex items-center gap-1 rounded border border-gray-200 bg-white px-2 py-1 text-xs font-medium text-gray-700 shadow-sm transition-colors hover:bg-gray-50">
                                  <PencilIcon className="h-3 w-3" />
                                  Edit
                                </button>
                                <button
                                  type="button"
                                  onClick={() => handleDeleteCohort(cohort)}
                                  className="inline-flex items-center gap-1 rounded border border-red-200 bg-white px-2 py-1 text-xs font-medium text-red-700 shadow-sm transition-colors hover:bg-red-50">
                                  <TrashIcon className="h-3 w-3" />
                                  Delete
                                </button>
                              </div>
                            </div>
                          ))}
                          {payrollCohorts.length === 0 && (
                            <div className="py-8 text-center">
                              <p className="text-sm text-gray-500">No payroll cohorts found.</p>
                            </div>
                          )}
                        </div>
                      </div>
                    ) : (
                      /* Cohort Form */
                      <form onSubmit={handleCohortSubmit} className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700">
                            Cohort Name *
                          </label>
                          <input
                            type="text"
                            value={cohortFormData.name}
                            onChange={(e) => setCohortFormData(prev => ({ ...prev, name: e.target.value }))}
                            className="mt-1 block w-full border border-gray-200 bg-white py-2 pl-3 pr-3 text-sm transition-colors hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                            placeholder="e.g., Hearsay, Kekoa Tree"
                            required
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700">
                            Description
                          </label>
                          <textarea
                            value={cohortFormData.description}
                            onChange={(e) => setCohortFormData(prev => ({ ...prev, description: e.target.value }))}
                            className="mt-1 block w-full border border-gray-200 bg-white py-2 pl-3 pr-3 text-sm transition-colors hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                            rows="3"
                            placeholder="Optional description for this cohort"
                          />
                        </div>
                        <div className="flex justify-end gap-2 pt-4">
                          <button
                            type="button"
                            onClick={() => setEditingCohort(null)}
                            className="inline-flex items-center gap-1.5 border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50">
                            Cancel
                          </button>
                          <button
                            type="submit"
                            disabled={cohortModalLoading}
                            className="inline-flex items-center gap-1.5 border border-indigo-600 bg-indigo-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-indigo-500 disabled:opacity-50">
                            {cohortModalLoading ? 'Saving...' : (editingCohort && editingCohort._id ? 'Update' : 'Create')}
                          </button>
                        </div>
                      </form>
                    )}
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </>
  );
}
